// Public activity row. Derives the design-card fields from a SubmittedAccountOp
// using the same balance-change / status helpers the legacy summary uses, then
// renders the Home v2 design card (ActivityRow) so public matches private.
import React, { useMemo } from 'react'
import { useModalize } from 'react-native-modalize'

import { ZeroAddress } from 'ethers'

import { Network } from '@ambire-common/interfaces/network'
import { AccountOpStatus } from '@ambire-common/libs/accountOp/types'
import { getUiType } from '@common/utils/uiType'
import {
  formatBalanceChangeAmount,
  getPresentationalStatus,
  getSummaryBalanceChanges
} from '@web/modules/settings/components/TransactionHistory/SubmittedTransactionSummary/helpers'
import SummaryDetailsSheet from '@web/modules/settings/components/TransactionHistory/SubmittedTransactionSummary/SummaryDetailsSheet'
import { SubmittedAccountOpLike } from '@web/modules/settings/components/TransactionHistory/SubmittedTransactionSummary/types'
import useSpeedUpTransaction from '@web/modules/settings/components/TransactionHistory/SubmittedTransactionSummary/useSpeedUpTransaction'
import useNetworksControllerState from '@web/hooks/useNetworksControllerState'
import useSelectedAccountControllerState from '@web/hooks/useSelectedAccountControllerState'

const { isTab } = getUiType()

import { ActivityKind } from '../tokens'
import useFeeFormatted from '@common/hooks/useFeeFormatted'
import ActivityRow from './ActivityRow'

const LABEL: Record<ActivityKind, string> = {
  swap: 'Swapped',
  send: 'Sent',
  receive: 'Received',
  shield: 'Shielded',
  unshield: 'Unshielded',
  other: 'Transaction'
}

const PublicActivityRow: React.FC<{ op: SubmittedAccountOpLike }> = ({ op }) => {
  const { networks } = useNetworksControllerState()
  const { account: selectedAccount } = useSelectedAccountControllerState()
  const { ref: sheetRef, open: openBottomSheet, close: closeBottomSheet } = useModalize()
  // Same action the detail sheet's footer offers, surfaced on the row itself so
  // users don't have to discover it by opening the sheet. Only for the selected
  // account, since the new request is built against it.
  const { shouldShowSpeedUp, handleSpeedUpTransaction } = useSpeedUpTransaction({
    submittedAccountOp: op
  })
  const canSpeedUp = shouldShowSpeedUp && selectedAccount?.addr === op.accountAddr
  const network = useMemo<Network | undefined>(
    () => networks.find((n: Network) => n.chainId === op.chainId),
    [networks, op.chainId]
  )

  const changes = useMemo(() => getSummaryBalanceChanges(op), [op])
  const nonFee = useMemo(() => changes.filter((c) => c.iconType !== 'gasTank'), [changes])

  /**
   * True when the op moved nothing: no token in or out, and no native value in
   * any call. Its only balance change is then the NATIVE gas it burned.
   *
   * That leg must not be read as a transfer. An ERC20 approve — the middle step
   * of every permit swap — has exactly this shape, and counting its gas as an
   * amount labelled it "Sent -42.94 PLS": the fee presented as if the user had
   * sent it to someone.
   */
  const isFeeOnly = useMemo(() => {
    const movesNativeValue = (op.calls || []).some((call) => (call.value ?? 0n) > 0n)
    const hasTokenChange = nonFee.some(
      (change) => change.address.toLowerCase() !== ZeroAddress.toLowerCase()
    )

    return !movesNativeValue && !hasTokenChange
  }, [op.calls, nonFee])

  /** Every call is an `approve(address,uint256)` — named by its selector, since
   *  the backend labels no step as an approval. */
  const isApprovalOnly = useMemo(() => {
    const calls = op.calls || []

    return calls.length > 0 && calls.every((call) => !!call.data?.startsWith('0x095ea7b3'))
  }, [op.calls])

  const kind: ActivityKind = useMemo(() => {
    // PRIMARY signal: a swap/bridge op always carries `meta.swapTxn` (set by the
    // swapAndBridge controller). Balance-change sign alone misses gasless / CCTP
    // cross-chain swaps, whose source-chain op only shows the outgoing leg and
    // would otherwise be mislabeled "Sent".
    // A relayer-broadcast swap has no AccountOp of ours to carry `swapTxn` — it
    // is discovered as an external txn — so it is flagged separately.
    if (op.meta?.swapTxn || op.meta?.isRelayerBroadcastSwap) return 'swap'
    // Checked before the sign-based branches below, which would otherwise read
    // the gas leg as an outgoing transfer.
    if (isFeeOnly) return 'other'
    const hasPos = nonFee.some((c) => c.balanceChange > 0n)
    const hasNeg = nonFee.some((c) => c.balanceChange < 0n)
    if (hasPos && hasNeg) return 'swap'
    if (hasNeg) return 'send'
    if (hasPos) return 'receive'
    return 'other'
  }, [nonFee, op.meta?.swapTxn, op.meta?.isRelayerBroadcastSwap, isFeeOnly])

  // For a swap show the token spent (negative leg) so "Swapped -0.1 USDC" reads
  // sensibly even when the received leg lands on another chain (CCTP). Fall back
  // to the first change for non-swaps.
  const primary = useMemo(() => {
    // Nothing moved, so there is no amount to show — the fee has its own row.
    if (isFeeOnly) return undefined

    return kind === 'swap' && nonFee.some((c) => c.balanceChange < 0n)
      ? nonFee.find((c) => c.balanceChange < 0n)
      : nonFee[0]
  }, [kind, nonFee, isFeeOnly])

  const { feeText, isLoading: feeLoading } = useFeeFormatted(op.gasFeePayment, network)

  if (!network) return null

  return (
    <>
      <ActivityRow
        data={{
          id: op.id || op.txnId || '',
          kind,
          kindLabel: isApprovalOnly ? 'Approved' : LABEL[kind],
          amount: primary ? formatBalanceChangeAmount(primary) : undefined,
          tokenSymbol: primary?.symbol,
          tokenAddress: primary?.address,
          chainId: op.chainId,
          status: getPresentationalStatus(op) ?? AccountOpStatus.Pending,
          feeText,
          isFeeLoading: feeLoading,
          networkName: network.name,
          timestampMs: op.timestamp || 0,
          explorerUrl: network.explorerUrl,
          txHash: op.txnId,
          onSpeedUp: canSpeedUp ? handleSpeedUpTransaction : undefined,
          onPress: openBottomSheet
        }}
      />
      {/* Tap the row → same humanized detail sheet the legacy popup used. */}
      <SummaryDetailsSheet
        sheetRef={sheetRef}
        closeBottomSheet={closeBottomSheet}
        modalType={isTab ? 'modal' : 'bottom-sheet'}
        submittedAccountOp={op}
        network={network}
        size="md"
        defaultType="full-info"
      />
    </>
  )
}

export default React.memo(PublicActivityRow)
